import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import logo from './images/lok-logo.png';

// Contact API. Defaults to the deployed backend; overridden to localhost for
// local dev via REACT_APP_API_URL in .env.development.
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.lok.tech';

// Field limits mirror the backend caps.
const NAME_MAX = 200;
const DESC_MAX = 4000;

// validContact mirrors the server's validation so bad formats fail instantly,
// with no round-trip. The backend re-validates — this is for UX, not security.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function validContact(method, value) {
  const v = value.trim();
  if (method === 'email') return v.length <= 254 && EMAIL_RE.test(v);
  // phone: optional leading +, 7–15 digits, common separators allowed.
  if (!/^\+?[0-9\s\-.()]+$/.test(v)) return false;
  const digits = (v.match(/\d/g) || []).length;
  return digits >= 7 && digits <= 15;
}

// Rotating phrase that lives inside the hero's parentheses.
const FITS = ['your idea', 'your team', 'your workflow', 'your users', 'your world'];

const SERVICES = [
  {
    id: 'products',
    title: 'Custom products',
    body:
      'The thing that does not exist yet. We take a rough idea to a working product — scoped, designed, and built to run in the real world.',
    tags: ['Strategy', 'Prototype', 'Launch'],
  },
  {
    id: 'apps',
    title: 'Web & mobile apps',
    body:
      'Interfaces people actually want to use, on the web and in their pocket. Built to be fast, accessible, and easy to change later.',
    tags: ['UI/UX', 'Web', 'Mobile'],
  },
  {
    id: 'automation',
    title: 'Automation & integrations',
    body:
      'The glue between the tools you already pay for. We wire up your systems so the repetitive work stops landing on someone’s desk.',
    tags: ['Workflows', 'Integrations', 'Reporting'],
  },
  {
    id: 'partner',
    title: 'Ongoing engineering',
    body:
      'On call for the long haul — new features, fixes, and honest advice about what to build next and what to leave alone.',
    tags: ['Support', 'Maintenance', 'Advisory'],
  },
];

const PROJECTS = [
  {
    name: 'Ruvo',
    url: 'https://ruvo.app',
    host: 'ruvo.app',
    tag: 'Productivity',
    body: 'Project management for teams that work with AI — one source of truth for the people and the agents.',
  },
  {
    name: 'Kalkalok',
    url: 'https://kalkalok.app',
    host: 'kalkalok.app',
    tag: 'Finance',
    body: 'Personal money management that turns a messy budget into clear goals. Smart money, clear future.',
  },
  {
    name: 'Pirate Wars',
    url: 'https://piratewars.app',
    host: 'piratewars.app',
    tag: 'Game',
    body: 'Real-time multiplayer naval combat — command a ship, raid rivals, and rule a 3D ocean.',
  },
  {
    name: 'Fasting',
    url: 'https://fasting.lok.tech',
    host: 'fasting.lok.tech',
    tag: 'Health',
    body: 'A privacy-first intermittent fasting tracker. Everything stays on your iPhone — no accounts, no servers.',
  },
];

const PROCESS = [
  { n: '01', title: 'Listen', body: 'We start with your problem, not our stack. What breaks, who feels it, what a fix is worth.' },
  { n: '02', title: 'Shape', body: 'A tight plan: what we build first, what we skip, and what done looks like. No 80-page decks.' },
  { n: '03', title: 'Build', body: 'Short cycles, working software you can click every week. You are never guessing about progress.' },
  { n: '04', title: 'Ship & tend', body: 'We launch, watch it in the wild, and stay close to keep it healthy as you grow.' },
];

function Check() {
  return (
    <svg
      className="ico-check"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12.5l4 4L19 7" />
    </svg>
  );
}

function Bracketed({ children }) {
  return (
    <span className="bracketed">
      <span className="paren paren-l">(</span>
      {children}
      <span className="paren paren-r">)</span>
    </span>
  );
}

function KineticHead() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches || paused) return undefined;
    const t = setInterval(() => setI((v) => (v + 1) % FITS.length), 2600);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <h1 className="hero-head">
      We make software fit
      <span
        className="fit-slot"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <span className="paren paren-l" aria-hidden="true">(</span>
        <span key={FITS[i]} className="fit-word">{FITS[i]}</span>
        <span className="paren paren-r" aria-hidden="true">)</span>
      </span>
    </h1>
  );
}

/* Products marquee — auto-scrolls by nudging the container's own scroll
   position, so hovering (which pauses it) lets the user scroll by hand. */
function ProductMarquee() {
  const ref = useRef(null);
  const paused = useRef(false);
  const pos = useRef(0); // float accumulator — scrollLeft rounds to int, so we can't += 0.5 on it

  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    pos.current = el.scrollLeft;
    let raf;
    const tick = () => {
      if (!paused.current) {
        const half = el.scrollWidth / 2;
        if (half > 0) {
          pos.current += 0.6;
          if (pos.current >= half) pos.current -= half;
          el.scrollLeft = pos.current;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const hold = () => { paused.current = true; };
  const release = () => {
    const el = ref.current;
    if (el) pos.current = el.scrollLeft; // resume from wherever the user scrolled to
    paused.current = false;
  };

  return (
    <div
      className="marquee"
      role="region"
      aria-label="Our products"
      ref={ref}
      onPointerEnter={hold}
      onPointerLeave={release}
      onFocusCapture={hold}
      onBlurCapture={release}
    >
      <div className="marquee-track">
        {[...PROJECTS, ...PROJECTS].map((p, i) => {
          const dup = i >= PROJECTS.length;
          return (
            <a
              className={'proj-card' + (dup ? ' is-dup' : '')}
              href={p.url}
              target="_blank"
              rel="noreferrer"
              key={p.host + (dup ? '-2' : '')}
              aria-hidden={dup || undefined}
              tabIndex={dup ? -1 : undefined}
            >
              <span className="proj-top">
                <span className="proj-kicker">{p.tag}</span>
                <span className="proj-arrow" aria-hidden="true">&#8599;</span>
              </span>
              <span className="proj-title">{p.name}</span>
              <span className="proj-body">{p.body}</span>
              <span className="proj-host">{p.host}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

/* Contact wizard — the lead fills everything across two steps. Each step
   reaches the backend and is persisted: step 1 creates the lead (pending) and
   returns an id + update token the client holds; step 2 completes it. The
   backend mints a short confirmation link (delivery is added later); opening it
   marks the request confirmed and lands on the thank-you page (#confirmed). */
function ContactForm() {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState('email');
  const [value, setValue] = useState('');
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');

  // Backend correlation: the lead's row id and its opaque update token, both
  // handed back by step 1 and replayed on later steps.
  const [leadId, setLeadId] = useState(null);
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isEmail = method === 'email';

  // postJSON returns the parsed body, throwing a message-bearing error on !ok.
  const postJSON = async (url, opts) => {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...opts,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Something went wrong. Please try again.');
    return data;
  };

  // Step 1 → 2: create the lead the first time, or update its contact on a
  // return trip from Back, then advance.
  const toDetails = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!validContact(method, value)) {
      setError(isEmail ? 'Enter a valid email address.' : 'Enter a valid phone number.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      if (!leadId) {
        const data = await postJSON(`${API_BASE}/v1/leads`, {
          method: 'POST',
          body: JSON.stringify({
            contact_method: method,
            contact_value: value.trim(),
            origin: window.location.origin,
          }),
        });
        setLeadId(data.id);
        setToken(data.token);
      } else {
        await postJSON(`${API_BASE}/v1/leads/${leadId}`, {
          method: 'PATCH',
          body: JSON.stringify({ token, contact_method: method, contact_value: value.trim() }),
        });
      }
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Step 2: complete the submission. The row moves to 'submitted' and the
  // confirmation link is minted server-side.
  const submit = async (e) => {
    e.preventDefault();
    if (busy || !leadId) return;
    if (!name.trim()) {
      setError('Please tell us your name.');
      return;
    }
    if (!desc.trim()) {
      setError('Please describe what we can build for you.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      await postJSON(`${API_BASE}/v1/leads/${leadId}`, {
        method: 'PATCH',
        body: JSON.stringify({ token, name: name.trim(), description: desc.trim(), complete: true }),
      });
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Start over from a clean slate so the next submission creates a fresh lead
  // rather than patching the finished one.
  const startOver = () => {
    setLeadId(null);
    setToken(null);
    setError('');
    setStep(1);
  };

  const stepClass = (n) => (step === n ? 'is-current' : step > n ? 'is-done' : '');

  return (
    <div className="contact-card">
      {step < 3 && (
        <ol className="stepper" aria-hidden="true">
          <li className={stepClass(1)}>
            <span className="dot">{step > 1 ? <Check /> : '1'}</span> Contact
          </li>
          <li className="step-sep" />
          <li className={stepClass(2)}>
            <span className="dot">2</span> Details
          </li>
        </ol>
      )}

      {step === 1 && (
        <form className="lead-form wizard-step" onSubmit={toDetails} noValidate key="s1">
          <div className="seg" role="group" aria-label="How should we reach you?">
            <button
              type="button"
              className={'seg-btn' + (isEmail ? ' is-active' : '')}
              aria-pressed={isEmail}
              onClick={() => { setMethod('email'); setError(''); }}
            >
              Email
            </button>
            <button
              type="button"
              className={'seg-btn' + (!isEmail ? ' is-active' : '')}
              aria-pressed={!isEmail}
              onClick={() => { setMethod('phone'); setError(''); }}
            >
              Phone
            </button>
          </div>

          <label className="field">
            <span className="field-label">{isEmail ? 'Your email' : 'Your phone'}</span>
            <input
              key={method}
              type={isEmail ? 'email' : 'tel'}
              inputMode={isEmail ? 'email' : 'tel'}
              autoComplete={isEmail ? 'email' : 'tel'}
              placeholder={isEmail ? 'you@company.com' : '+972 50 000 0000'}
              value={value}
              onChange={(e) => { setValue(e.target.value); if (error) setError(''); }}
              aria-invalid={error ? true : undefined}
              maxLength={254}
              required
            />
          </label>

          {error && <p className="form-error" role="alert">{error}</p>}
          <button type="submit" className="btn btn-solid form-submit" disabled={busy}>
            {busy ? 'Saving…' : 'Continue'}
          </button>
        </form>
      )}

      {step === 2 && (
        <form className="lead-form wizard-step" onSubmit={submit} noValidate key="s2">
          <label className="field">
            <span className="field-label">Your name</span>
            <input
              type="text"
              autoComplete="name"
              placeholder="Jane Cohen"
              value={name}
              onChange={(e) => { setName(e.target.value); if (error) setError(''); }}
              maxLength={NAME_MAX}
              required
            />
          </label>
          <label className="field">
            <span className="field-label">What can we build for you?</span>
            <textarea
              placeholder="What are you trying to do, who is it for, and what does done look like?"
              value={desc}
              onChange={(e) => { setDesc(e.target.value); if (error) setError(''); }}
              maxLength={DESC_MAX}
              required
            />
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="form-row">
            <button type="button" className="btn btn-ghost" onClick={() => setStep(1)} disabled={busy}>Back</button>
            <button type="submit" className="btn btn-solid" disabled={busy}>
              {busy ? 'Sending…' : 'Send request'}
            </button>
          </div>
        </form>
      )}

      {step === 3 && (
        <div className="sent wizard-step" key="s3">
          <span className="sent-badge"><Check /></span>
          <h3 className="sent-title">Check your {isEmail ? 'inbox' : 'phone'}</h3>
          <p className="sent-body">
            We sent a confirmation link to <span className="to">{value}</span>. Open it to approve
            your request — that final click keeps spam out and your details safe.
          </p>
          <div className="sent-actions">
            <button type="button" className="linklike" onClick={startOver}>
              Start over
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* Thank-you page — where the confirmation link lands. The request is already
   in; this only acknowledges the approval. */
function ThankYouPage() {
  return (
    <div className="continue">
      <div className="continue-card thanks-card">
        <img className="continue-logo" src={logo} alt="Lok" />
        <p className="eyebrow"><Bracketed><Check /> request confirmed</Bracketed></p>
        <h1 className="continue-head">Thank you</h1>
        <p className="continue-sub">
          Your request is confirmed and on its way to us. Expect a reply from a real person,
          not an autoresponder.
        </p>
        <a className="btn btn-solid" href="#top">Back to the site</a>
      </div>
    </div>
  );
}

function getRoute() {
  return window.location.hash.replace(/\?.*$/, '') === '#confirmed' ? 'thanks' : 'home';
}

export default function App() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [route, setRoute] = useState(getRoute());

  const onScroll = useCallback(() => setScrolled(window.scrollY > 8), []);
  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  useEffect(() => {
    const onHash = () => setRoute(getRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  if (route === 'thanks') return <ThankYouPage />;

  return (
    <div className="site">
      <a className="skip" href="#main">Skip to content</a>

      <header className={'nav' + (scrolled ? ' is-scrolled' : '')}>
        <a className="brand" href="#top" aria-label="Lok, home">
          <img className="brand-mark" src={logo} alt="Lok" />
        </a>

        <nav className={'nav-links' + (menuOpen ? ' is-open' : '')} aria-label="Primary">
          <a href="#services" onClick={closeMenu}>Services</a>
          <a href="#process" onClick={closeMenu}>How we work</a>
          <a href="#work" onClick={closeMenu}>Products</a>
          <a className="nav-cta" href="#contact" onClick={closeMenu}>Start a project</a>
        </nav>

        <button
          className={'burger' + (menuOpen ? ' is-open' : '')}
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span /><span /><span />
        </button>
      </header>

      <main id="main">
        <section className="hero" id="top">
          <KineticHead />
          <p className="hero-sub">
            Lok is a software studio. We design and build custom products, web and mobile
            apps, and the automations that tie your tools together &mdash; end to end, for the
            people who will actually use them.
          </p>
          <div className="hero-actions">
            <a className="btn btn-solid" href="#contact">Start a project</a>
            <a className="btn btn-ghost" href="#services">See what we do</a>
          </div>
        </section>

        <section className="services" id="services">
          <div className="section-head">
            <p className="eyebrow"><Bracketed>01&nbsp;&mdash;&nbsp;services</Bracketed></p>
            <h2 className="section-title">What we build</h2>
          </div>
          <div className="svc-grid">
            {SERVICES.map((s) => (
              <article className="svc" key={s.id}>
                <h3 className="svc-title">{s.title}</h3>
                <p className="svc-body">{s.body}</p>
                <ul className="svc-tags">
                  {s.tags.map((t) => <li key={t}>{t}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="process" id="process">
          <div className="section-head">
            <p className="eyebrow eyebrow-dark"><Bracketed>02&nbsp;&mdash;&nbsp;how we work</Bracketed></p>
            <h2 className="section-title">Four honest steps</h2>
          </div>
          <ol className="steps">
            {PROCESS.map((p) => (
              <li className="step" key={p.n}>
                <span className="step-n">{p.n}</span>
                <div className="step-text">
                  <h3 className="step-title">{p.title}</h3>
                  <p>{p.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="work" id="work">
          <div className="section-head">
            <p className="eyebrow"><Bracketed>03&nbsp;&mdash;&nbsp;in-house</Bracketed></p>
            <h2 className="section-title">Our own products</h2>
            <p className="work-lead">
              The clearest proof of how we work is what we build for ourselves &mdash;
              designed, coded, and run end to end.
            </p>
          </div>
          <ProductMarquee />
        </section>

        <section className="contact" id="contact">
          <div className="contact-inner">
            <div className="contact-copy">
              <p className="eyebrow eyebrow-dark"><Bracketed>04&nbsp;&mdash;&nbsp;contact</Bracketed></p>
              <h2 className="contact-head">Tell us what does not exist yet.</h2>
              <p className="contact-sub">
                Fill in your project in two quick steps. We send a confirmation link to make
                sure it is really you &mdash; one click and it lands with us, spam-free.
              </p>
            </div>
            <ContactForm />
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-brand">
          <img className="footer-mark" src={logo} alt="Lok" />
          <p className="footer-tag">Software, made to fit.</p>
        </div>
        <div className="footer-cols">
          <div>
            <span className="footer-h">Go to</span>
            <a href="#services">Services</a>
            <a href="#process">How we work</a>
            <a href="#work">Products</a>
          </div>
          <div>
            <span className="footer-h">Contact</span>
            <a href="#contact">Start a project</a>
            <a href="mailto:hello@lok.tech">hello@lok.tech</a>
          </div>
        </div>
        <p className="footer-fine">&copy; {new Date().getFullYear()} Lok. All rights reserved.</p>
      </footer>
    </div>
  );
}
